/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable } from 'rxjs';
import { SNMPEnum } from '../enums/api';

@Injectable()
export class SNMPConfigurationApiService {

    constructor(
        private apiService: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Add SNMP configuration via API
     * @param data
     * @param options
     */
    public setSNMP(data: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.apiService.put(
                SNMPEnum.PUT_SNMP_SETTINGS,
                data,
                moduleAuth,
                false
            ).subscribe({
                next: (response: any): void => {
                    observer.next(response);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });

    }

    /**
     * Get SNMP configuration from API
     */
    public getSNMP(options?: any): Observable<any> {
        return new Observable((observer) => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.apiService.get(
                SNMPEnum.GET_SNMP_SETTINGS,
                moduleAuth,
                false
            ).subscribe({
                next: (response: any): void => {
                    observer.next(response);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Get all users from config.
     * @param options
     */
    public getAllSNMPUsers(options?: any): Observable<any> {
        return new Observable((observer) => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.apiService.get(
                SNMPEnum.GET_SNMP_USERS,
                moduleAuth,
            ).subscribe({
                next: (response: any): void => {
                    observer.next(response);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Add user to SNMP config.
     * @param user
     * @param options
     */
    public addSNMPUser(user: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.apiService.post(
                SNMPEnum.POST_SNMP_USER,
                user,
                moduleAuth,
            ).subscribe({
                next: (response: any): void => {
                    observer.next(response);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Edit user SNMP config.
     * @param id
     * @param user
     * @param options
     */
    public editSNMPUser(id: string, user: any, options?: any): Observable<any> {
        return new Observable((observer) => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.apiService.put(
                SNMPEnum.PUT_SNMP_USER(id),
                user,
                moduleAuth,
            ).subscribe({
                next: (response: any): void => {
                    observer.next(response);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Delete user from SNMP config.
     * @param id
     * @param options
     */
    public deleteSNMPUser(id: string, options?: any): Observable<any> {
        return new Observable((observer) => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.apiService.delete(
                SNMPEnum.DELETE_SNMP_USER(id),
                moduleAuth,
            ).subscribe({
                next: (response: any): void => {
                    observer.next(response);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }
}
